/**
 * OrderDetailsModal Component
 * Modal wrapper for displaying order details table in popups
 */

import React from 'react';
import type { OrderDetailsModalProps } from '../../types/components';
import { useGetJobOrdersQuery } from '../../store/slices/apiSlice';
import { OrderDetailsTable } from './OrderDetailsTable';
import { Button } from './Button';
import { X } from 'lucide-react';

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  jobId,
  title = 'Order Details',
  mode = 'view',
  maxHeight = '80vh'
}) => {
  // Fetch orders for the job
  const { data: ordersData, isLoading, isError } = useGetJobOrdersQuery(jobId, {
    skip: !isOpen || !jobId
  });

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const orders = Array.isArray(ordersData) ? ordersData : (ordersData?.results ?? []);

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="inline-block w-full max-w-6xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {title}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              icon={<X className="w-4 h-4" />}
            >
              Close
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 120px)' }}>
            {isError ? (
              <div className="text-center py-8 text-red-600">
                <div className="text-sm">Failed to load orders</div>
                <div className="text-xs mt-2">Please try again later</div>
              </div>
            ) : (
              <OrderDetailsTable
                orders={orders}
                loading={isLoading}
                mode={mode}
                rackContext="order_modal"
                className="w-full"
              />
            )}
          </div>

          {/* Footer (if needed) */}
          <div className="flex justify-end px-6 py-4 border-t border-gray-200">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

OrderDetailsModal.displayName = 'OrderDetailsModal';
